/**
 * ChapterAnalyticsTab — Chart.js powered analytics for chapters
 * 6 KPI cards + 10 visualizations using react-chartjs-2
 */

import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut, Pie, PolarArea } from 'react-chartjs-2';
import { Box, CircularProgress, Typography } from '@mui/material';
import apiService from '../../services/api';
import MetricCard from '../common/MetricCard';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { rangeDays, formatCustomLabel, toYmd } from '../common/timeRange';
import { colors } from '../../theme/designTokens';
import './ChapterAnalyticsTab.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler
);

const CHART_COLORS = [
  colors.deepTeal[500],
  colors.warmGold[500],
  colors.deepTeal[300],
  colors.warmGold[300],
  colors.slateGray[500],
  colors.deepTeal[700],
  colors.warmGold[700],
  colors.slateGray[300],
  '#146152',
  '#7D1935',
];

const CHART_COLORS_ALPHA = CHART_COLORS.map(c => c + '99');

interface ChapterAnalyticsData {
  summary: {
    totalChapters: number;
    uniqueUsers: number;
    avgWordCount: number;
    completedChapters: number;
    draftChapters: number;
    aiTransformed: number;
    avgRecordingDuration: number;
    completionRate: number;
  };
  trends: Array<{ date: string; count: number; uniqueUsers: number; avgWordCount: number }>;
  writingStyles: Array<{ name: string; count: number }>;
  illustrationStyles: Array<{ name: string; count: number }>;
  inputMethods: Array<{ method: string; count: number }>;
  storyLengths: Array<{ length: string; count: number }>;
  perspectives: Array<{ perspective: string; count: number }>;
  moods: Array<{ mood: string; count: number }>;
  wordCountDistribution: Array<{ bucket: string; count: number }>;
  topAuthors: Array<{ email: string; chapterCount: number; totalWords: number }>;
  aiImages: Array<{ date: string; requested: number; generated: number }>;
}

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' as const, labels: { padding: 12, usePointStyle: true, font: { size: 11 } } },
  },
};

const ChapterAnalyticsTab: React.FC = () => {
  const [data, setData] = useState<ChapterAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useTimeRangeParam({ type: '30d' }, { prefix: 'chapters' });
  // The endpoint takes a plain day count. "All Time" used to be a 3650-day pill;
  // Custom now covers any span the operator actually wants.
  const timeRange = String(rangeDays(range));
  // ...and a day count counts BACK FROM NOW. A custom window that ends in the
  // past is therefore re-anchored to today by the server, so the pill can read
  // "Jan 1 – Jan 31" over the last 31 days' data. Say so rather than let the
  // pill lie. Same disclosure as RevenueSubscriptionsPage, which shares the
  // `days=` dialect.
  const customEndsInPast = range.type === 'custom' && range.to < toYmd(new Date());

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const result = await apiService.getDetailedChapterAnalytics(timeRange);
      setData(result);
    } catch (error) {
      console.error('Failed to load chapter analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const filter = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75, mb: 2.5 }}>
      <div className="analytics-time-picker" style={{ marginBottom: 0 }}>
        <TimeRangeFilter value={range} onChange={setRange} onRefresh={loadAnalytics} refreshing={loading} />
      </div>
      {customEndsInPast && (
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '11.5px',
            color: 'text.secondary',
            maxWidth: 420,
            lineHeight: 1.4,
          }}
        >
          These figures measure back from today. {formatCustomLabel(range)} is
          {' '}{timeRange} days long, so the last {timeRange} days — ending today —
          are what is shown.
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <div className="chapter-analytics">
        {filter}
        <div className="chapter-analytics-loading">
          <CircularProgress size={32} sx={{ mr: 2 }} />
          Loading analytics...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="chapter-analytics">
        {filter}
        <div className="chapter-analytics-loading">No analytics data available</div>
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className="chapter-analytics">
      {/* Time Range Picker */}
      {filter}

      {/* 6 KPI Cards */}
      <div className="chapter-analytics-kpis">
        <MetricCard title="Total Chapters" value={s.totalChapters} icon="book" color="content" />
        <MetricCard title="Active Authors" value={s.uniqueUsers} icon="people" color="users" />
        <MetricCard title="AI Transformation Rate" value={`${s.completionRate}%`} icon="smart_toy" color="content" />
        <MetricCard title="Avg Word Count" value={s.avgWordCount} icon="description" color="content" />
        <MetricCard title="Completion Rate" value={`${s.completionRate}%`} icon="check_circle" color="revenue" />
        <MetricCard title="Avg Recording" value={s.avgRecordingDuration > 0 ? `${Math.round(s.avgRecordingDuration / 60)}m` : 'N/A'} icon="mic" color="content" />
      </div>

      {/* 10 Charts */}
      <div className="chapter-analytics-charts">
        {/* 1. Chapters Created Over Time (Line) — wide */}
        <div className="chart-card chart-card--wide">
          <h4 className="chart-card-title">Chapters Created Over Time</h4>
          <div className="chart-card-canvas">
            <Line
              data={{
                labels: data.trends.map(t => t.date),
                datasets: [{
                  label: 'Chapters',
                  data: data.trends.map(t => t.count),
                  borderColor: colors.deepTeal[500],
                  backgroundColor: colors.deepTeal[500] + '20',
                  fill: true,
                  tension: 0.3,
                  pointRadius: 3,
                }],
              }}
              options={{
                ...commonOptions,
                scales: {
                  x: { ticks: { maxTicksLimit: 15, font: { size: 10 } } },
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>
        </div>

        {/* 2. Input Method Distribution (Doughnut) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Input Method Distribution</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <Doughnut
              data={{
                labels: data.inputMethods.map(m => m.method || 'Unknown'),
                datasets: [{ data: data.inputMethods.map(m => m.count), backgroundColor: CHART_COLORS_ALPHA.slice(0, data.inputMethods.length), borderColor: CHART_COLORS.slice(0, data.inputMethods.length), borderWidth: 2 }],
              }}
              options={commonOptions}
            />
          </div>
        </div>

        {/* 3. Writing Style Popularity (Horizontal Bar) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Writing Style Popularity</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <Bar
              data={{
                labels: data.writingStyles.map(s => s.name),
                datasets: [{ label: 'Chapters', data: data.writingStyles.map(s => s.count), backgroundColor: colors.deepTeal[500] + '80', borderColor: colors.deepTeal[500], borderWidth: 1 }],
              }}
              options={{ ...commonOptions, indexAxis: 'y' as const, scales: { x: { beginAtZero: true } } }}
            />
          </div>
        </div>

        {/* 4. Story Length Distribution (Bar) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Story Length Distribution</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <Bar
              data={{
                labels: data.storyLengths.map(s => s.length),
                datasets: [{ label: 'Chapters', data: data.storyLengths.map(s => s.count), backgroundColor: [colors.warmGold[300] + '99', colors.warmGold[500] + '99', colors.warmGold[700] + '99'], borderColor: [colors.warmGold[300], colors.warmGold[500], colors.warmGold[700]], borderWidth: 1 }],
              }}
              options={{ ...commonOptions, scales: { y: { beginAtZero: true } } }}
            />
          </div>
        </div>

        {/* 5. Illustration Style Usage (Doughnut) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Illustration Style Usage</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <Doughnut
              data={{
                labels: data.illustrationStyles.map(s => s.name),
                datasets: [{ data: data.illustrationStyles.map(s => s.count), backgroundColor: CHART_COLORS_ALPHA.slice(0, data.illustrationStyles.length), borderColor: CHART_COLORS.slice(0, data.illustrationStyles.length), borderWidth: 2 }],
              }}
              options={commonOptions}
            />
          </div>
        </div>

        {/* 6. Mood Distribution (Polar Area) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Mood Distribution</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <PolarArea
              data={{
                labels: data.moods.map(m => m.mood),
                datasets: [{ data: data.moods.map(m => m.count), backgroundColor: CHART_COLORS_ALPHA.slice(0, data.moods.length) }],
              }}
              options={commonOptions}
            />
          </div>
        </div>

        {/* 7. Word Count Distribution (Bar histogram) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Word Count Distribution</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <Bar
              data={{
                labels: data.wordCountDistribution.map(w => w.bucket),
                datasets: [{ label: 'Chapters', data: data.wordCountDistribution.map(w => w.count), backgroundColor: colors.deepTeal[400] + '80', borderColor: colors.deepTeal[400], borderWidth: 1 }],
              }}
              options={{ ...commonOptions, scales: { y: { beginAtZero: true } } }}
            />
          </div>
        </div>

        {/* 8. Narrative Perspective Split (Pie) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Narrative Perspective</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <Pie
              data={{
                labels: data.perspectives.map(p => p.perspective?.replace('_', ' ') || 'Unknown'),
                datasets: [{ data: data.perspectives.map(p => p.count), backgroundColor: [colors.deepTeal[500] + '99', colors.warmGold[500] + '99', colors.slateGray[400] + '99'], borderColor: [colors.deepTeal[500], colors.warmGold[500], colors.slateGray[400]], borderWidth: 2 }],
              }}
              options={commonOptions}
            />
          </div>
        </div>

        {/* 9. Top Authors (Horizontal Bar) */}
        <div className="chart-card">
          <h4 className="chart-card-title">Top Authors</h4>
          <div className="chart-card-canvas chart-card-canvas--small">
            <Bar
              data={{
                labels: data.topAuthors.map(a => a.email?.split('@')[0] || 'Unknown'),
                datasets: [{ label: 'Chapters', data: data.topAuthors.map(a => a.chapterCount), backgroundColor: colors.warmGold[500] + '80', borderColor: colors.warmGold[500], borderWidth: 1 }],
              }}
              options={{ ...commonOptions, indexAxis: 'y' as const, scales: { x: { beginAtZero: true } } }}
            />
          </div>
        </div>

        {/* 10. AI Images Requested vs Generated (Stacked Bar) — wide */}
        {data.aiImages.length > 0 && (
          <div className="chart-card chart-card--wide">
            <h4 className="chart-card-title">AI Images: Requested vs Generated</h4>
            <div className="chart-card-canvas">
              <Bar
                data={{
                  labels: data.aiImages.map(a => a.date),
                  datasets: [
                    { label: 'Requested', data: data.aiImages.map(a => a.requested), backgroundColor: colors.deepTeal[500] + '80', stack: 'images' },
                    { label: 'Generated', data: data.aiImages.map(a => a.generated), backgroundColor: colors.warmGold[500] + '80', stack: 'images' },
                  ],
                }}
                options={{
                  ...commonOptions,
                  scales: {
                    x: { stacked: true, ticks: { maxTicksLimit: 15, font: { size: 10 } } },
                    y: { stacked: true, beginAtZero: true },
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChapterAnalyticsTab;
